/**
 * mutationLog.js — Dexie helpers for the offline mutation queue.
 *
 * Row shape:
 *   key          string   UUID primary key
 *   type         string   one of MUTATION_HANDLERS keys
 *   payload      object   opaque — backend validates at drain time
 *   status       string   'pending' | 'in_flight' | 'failed'
 *   created_at   number   Date.now() at append time
 *   attempts     number   incremented only on failed transitions
 *   last_error   string?  set on failed transition
 *   prior_state  any?     set by sub-task 2 for optimistic-write rollback
 *
 * Negative-int highlight id contract:
 *   highlight_create mutations use a negative local id as temporary identity.
 *   Sub-task 2 / item 94 replace this with the server-assigned positive id
 *   after a successful drain.
 *
 * No payload validators — backend rejects mismatched payloads at drain time,
 * which transitions the row to status='failed'.
 */

import Dexie from 'dexie';
import { db } from '../db/index';

export const MUTATION_STATUS = {
  PENDING: 'pending',
  IN_FLIGHT: 'in_flight',
  FAILED: 'failed',
};

/**
 * Generate a v4 UUID. Uses crypto.randomUUID() when available (modern
 * browsers and Node 14.17+); falls back to a hand-rolled v4 for jsdom
 * and older environments.
 */
export function generateMutationKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // RFC 4122 v4 fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Append a new mutation to the queue with status='pending'.
 *
 * @param {{ type: string, payload: object }} opts
 * @returns {Promise<object>} The written row.
 */
export async function appendMutation({ type, payload }) {
  const entry = {
    key: generateMutationKey(),
    type,
    payload,
    status: MUTATION_STATUS.PENDING,
    created_at: Date.now(),
    attempts: 0,
  };
  await db.table('mutation_log').put(entry);
  return entry;
}

/**
 * Return all pending mutations ordered by created_at ascending (FIFO).
 *
 * @param {{ limit?: number }} [opts]
 * @returns {Promise<object[]>}
 */
export async function listPendingMutations({ limit } = {}) {
  let col = db.table('mutation_log')
    .where('[status+created_at]')
    .between(
      [MUTATION_STATUS.PENDING, Dexie.minKey],
      [MUTATION_STATUS.PENDING, Dexie.maxKey],
    );
  if (limit != null) {
    col = col.limit(limit);
  }
  return col.toArray();
}

/**
 * Return all rows (for debugging / tests).
 *
 * @returns {Promise<object[]>}
 */
export async function listAllMutations() {
  return db.table('mutation_log').toArray();
}

/**
 * Transition a row to a new status. Increments attempts when going to 'failed'.
 *
 * @param {string} key
 * @param {string} nextStatus
 * @param {{ error?: string }} [opts]
 */
export async function transitionStatus(key, nextStatus, { error } = {}) {
  const row = await db.table('mutation_log').get(key);
  if (!row) return;
  const update = { status: nextStatus };
  if (nextStatus === MUTATION_STATUS.FAILED) {
    update.attempts = (row.attempts || 0) + 1;
    if (error != null) update.last_error = error;
  } else if (error != null) {
    update.last_error = error;
  }
  await db.table('mutation_log').update(key, update);
}

/**
 * Delete a single mutation row (called after successful drain).
 *
 * @param {string} key
 */
export async function deleteMutation(key) {
  await db.table('mutation_log').delete(key);
}

/**
 * Clear all mutation rows (called on user switch by item 95 / sub-task 3).
 */
export async function clearAllMutations() {
  await db.table('mutation_log').clear();
}
